import { batch } from 'react-redux';

// project imports
import gestopAPI, { gestopApiForExternalEndpoints } from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { ticketActions } from './slice';
import capabilities from './capabilities';

export const ApiTicketsGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/tickets/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketActions.setList(response?.data?.list));
                    dispatch(ticketActions.setCount(response?.data?.count));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data?.list);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketsCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('workmgmt/tickets/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiTicketsPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/tickets', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketsDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/tickets/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketsExportCollection = async ({
    exportType = 'allRows',
    format = 'pdf',
    columns = [],
    filters = [],
    orderBy = [],
    groupBy = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(ticketActions.startExporting());
    }

    return gestopAPI
        .post(
            'workmgmt/tickets/export',
            { exportType, format, columns, filters, orderBy, groupBy, includeSafeDeleted },
            { ...config, responseType: 'blob' }
        )
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(ticketActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }

                return response?.data;
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(ticketActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }

                return null;
            })
        );
};

export const ApiTicketsPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketActions.startLoading());
    }

    await gestopAPI
        .put(`workmgmt/tickets/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketsPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketActions.startLoading());
    }

    await gestopAPI
        .patch(`workmgmt/tickets/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketsDeleteItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketActions.startLoading());
    }

    await gestopAPI
        .post(`workmgmt/tickets/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketsRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketActions.startLoading());
    }

    await gestopAPI
        .post(`workmgmt/tickets/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketsExportItem = async ({ id, openFile = true, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(ticketActions.startExporting());
    }

    return gestopAPI
        .get(`workmgmt/tickets/export/${id}`, { ...config, responseType: 'blob' })
        .then((response) =>
            batch(() => {
                if (openFile) {
                    window.open(URL.createObjectURL(response?.data));
                }

                if (capabilities.app.includes('exporter')) {
                    dispatch(ticketActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }

                return response?.data;
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(ticketActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }

                return null;
            })
        );
};

export const ApiTicketsGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketActions.startLoading());
    }

    await gestopAPI
        .get(`workmgmt/tickets/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketsGenerateUrl = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    await gestopAPI
        .get(`workmgmt/tickets/generatesignlink/${id}`, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiTicketsGetUrl = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    await gestopAPI
        .get(`workmgmt/tickets/getsignlink/${id}`, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiTicketsDeleteSignLink = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    await gestopAPI
        .get(`workmgmt/tickets/deletesignlink/${id}`, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiTicketsExternalCheckSignLink = async ({ id, hash, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    await gestopApiForExternalEndpoints
        .get(`workmgmt/tickets/external/checksignlink/${hash}/${id}`, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiTicketsExternalSign = async ({ id, hash, data, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(ticketActions.startExporting());
    }

    await gestopApiForExternalEndpoints
        .post(`workmgmt/tickets/external/sign/${hash}/${id}`, data, { ...config, responseType: 'blob' })
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(ticketActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiTicketsFileManagerUpload = async ({ id, data = {}, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    await gestopAPI
        .post(`workmgmt/tickets/filemanager/upload/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' } // Specific content type for file requests
        })
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};
